/**
 * BillingSettings — Billing Overview + Business Group cards.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, CreditCard, Building2 } from 'lucide-react';
import { getStatusColor } from '../../lib/utils';

export default function BillingSettings({
  restaurantGroup,
  subscriptionStatus,
  PLANS,
  currentPlan,
  trialDaysRemaining,
  submitting,
  handleChangeBillingCycle,
  formatDate,
}) {
  if (!restaurantGroup) return null;

  return (
    <>
      {/* Billing Overview Card — data-settings-section="billing" (REQUIRED — do not remove) */}
      <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-settings-section="billing">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard size={20} className="text-orange-500" />
            Billing Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Current Plan */}
            <div className="p-4 bg-white rounded-lg border border-slate-100">
              <p className="text-sm text-slate-500 mb-1">Current Plan</p>
              <p className="text-xl font-bold text-slate-900">{currentPlan.name}</p>
              <p className="text-sm text-slate-600 mt-1">
                ${subscriptionStatus?.billing_cycle === 'yearly'
                  ? currentPlan.yearlyPrice
                  : currentPlan.monthlyPrice}
                /{subscriptionStatus?.billing_cycle === 'yearly' ? 'year' : 'month'}
              </p>
            </div>

            {/* Status */}
            <div className="p-4 bg-white rounded-lg border border-slate-100">
              <p className="text-sm text-slate-500 mb-1">Status</p>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  restaurantGroup.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : restaurantGroup.status === 'trial'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-slate-100 text-slate-800'
                }`}>
                  {restaurantGroup.status === 'trial' ? 'Free Trial' :
                   restaurantGroup.status.charAt(0).toUpperCase() + restaurantGroup.status.slice(1)}
                </span>
              </div>
              {restaurantGroup.status === 'trial' && subscriptionStatus?.trial_ends_at && (
                <p className="text-sm text-slate-600 mt-2">
                  {trialDaysRemaining} days remaining
                </p>
              )}
            </div>

            {/* Billing Cycle */}
            <div className="p-4 bg-white rounded-lg border border-slate-100">
              <p className="text-sm text-slate-500 mb-1">Billing Cycle</p>
              <p className="text-xl font-bold text-slate-900 capitalize">
                {subscriptionStatus?.billing_cycle || 'Monthly'}
              </p>
              {subscriptionStatus?.current_period_end && restaurantGroup.status === 'active' && (
                <p className="text-sm text-slate-600 mt-1">
                  Next billing: {formatDate(subscriptionStatus.current_period_end)}
                </p>
              )}
              {/* Change billing cycle button */}
              {subscriptionStatus?.stripe_subscription_id && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto mt-2 text-orange-600"
                  onClick={() => handleChangeBillingCycle(
                    subscriptionStatus?.billing_cycle === 'yearly' ? 'monthly' : 'yearly'
                  )}
                  disabled={submitting}
                  data-testid="change-billing-cycle-btn"
                >
                  Switch to {subscriptionStatus?.billing_cycle === 'yearly' ? 'monthly' : 'yearly'}
                </Button>
              )}
            </div>
          </div>

          {/* Trial Warning */}
          {restaurantGroup.status === 'trial' && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-amber-600 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-amber-800">
                  {trialDaysRemaining <= 0
                    ? 'Trial ending today!'
                    : trialDaysRemaining === 1
                      ? '1 day left in your trial'
                      : `${trialDaysRemaining} days left in your trial`}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {(() => {
                    const trialEndDate = formatDate(
                      subscriptionStatus?.trial_ends_at ||
                      restaurantGroup?.trial_expiry ||
                      restaurantGroup?.trial_ends_at
                    );
                    return trialEndDate
                      ? `Your free trial ends on ${trialEndDate}.`
                      : 'Your free trial is ending soon.';
                  })()}
                  {subscriptionStatus?.has_payment_method
                    ? ' Your card will be charged automatically.'
                    : ' Add a payment method to continue using ReStaff.'}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Business Group Info Card */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 size={20} className="text-slate-600" />
            Business Group
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900">{restaurantGroup.brand_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`status-badge ${getStatusColor(restaurantGroup.status)}`}>
                  {restaurantGroup.status === 'trial' ? '30-Day Trial' :
                   restaurantGroup.status.charAt(0).toUpperCase() + restaurantGroup.status.slice(1)}
                </span>
                <span className="text-sm text-slate-500">
                  {currentPlan.name}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
